import React, { FC, memo, ReactNode, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { usePopperTooltip } from 'react-popper-tooltip';
import { Config, PopperOptions } from 'react-popper-tooltip/dist/types';
import { CSSTransition } from 'react-transition-group';

import { requestTooltipPopperOptionsDefault, requestTooltipPopperOptionsLow } from '@/data/request/tooltip';
import { useRootElement } from '@/hooks/useRootElement';
import useWindowSize from '@/hooks/useWindowSize';

import cn from './style.module.sass';

interface IProps {
	config?: Config;
	popperOptions?: PopperOptions;
	title?: string;
	description?: string;
	isHalf?: boolean;
	children?: ReactNode;
}

const Tooltip: FC<IProps> = ({ config, title, description, children, isHalf }) => {
	const { width } = useWindowSize();

	const {
		correctPopperConfig,
		correctPlacement,
	}: { correctPopperConfig: PopperOptions; correctPlacement: 'right' | 'top' } = useMemo(
		() => ({
			correctPopperConfig:
				width >= 1480 ? requestTooltipPopperOptionsDefault(isHalf) : requestTooltipPopperOptionsLow,
			correctPlacement: width >= 1480 ? 'right' : 'top',
		}),
		[width]
	);

	const { getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip(
		{
			trigger: ['hover'],
			placement: correctPlacement,
			...config,
		},
		{
			strategy: 'absolute',
			...correctPopperConfig,
		}
	);

	const node = useRootElement('body');

	// eslint-disable-next-line react/jsx-no-useless-fragment
	if (!title && !description) return <>{children}</>;

	return (
		<>
			<div className={cn.tooltipRef} ref={setTriggerRef}>
				{children}
			</div>
			{node &&
				visible &&
				ReactDOM.createPortal(
					<CSSTransition
						in={visible}
						timeout={200}
						classNames={{
							enterActive: cn.fadeIn,
							exitActive: cn.fadeOut,
							exit: cn.fadeOut,
							exitDone: cn.fadeOut,
						}}
					>
						<div
							ref={setTooltipRef}
							{...getTooltipProps({ className: 'tooltip-container' })}
							className={cn.tooltip}
						>
							<div className={cn.tooltipContent}>
								<div className={cn.title}>{title}</div>
								<div className={cn.description} dangerouslySetInnerHTML={{ __html: description }} />
							</div>
						</div>
					</CSSTransition>,
					node
				)}
		</>
	);
};

export default memo(Tooltip);
